/**
 * `useMediaQuery` — subscribe to a CSS media query.
 *
 * We deliberately avoid AntD's `Grid.useBreakpoint` for boundary-sensitive
 * checks because its breakpoints use `min-width` semantics (`md` is true at
 * width ≥ 768) — so `!screens.md` is `< 768`, which is off-by-one vs the
 * spec's "≤ 768px = mobile" rule. With a hand-rolled `(max-width: 768px)`
 * query, width === 768 falls into the mobile branch as intended.
 *
 * SSR-safe: the initial value defers to `false` when `window` is missing,
 * matching the desktop branch on the server and then re-syncing on mount.
 */

import { useEffect, useState } from "react";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    // Re-sync once on mount in case the query was evaluated against a
    // stale `window` size (e.g. orientation change between SSR and hydrate).
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
